import React, { useCallback, useEffect, useMemo, useState } from "react";
import { deleteFilesServer, getChatImagesByPath } from "Config/API/get";

import DeleteIcon from "@mui/icons-material/Delete";
import Divider from "@mui/material/Divider";
import DownloadIcon from "@mui/icons-material/Download";
import FilePreviewPopup from "Components/Popup/filePreviewPopup/FilePreviewPopup";
import { IconButton } from "@mui/material";
import { Link } from "react-router-dom";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import RenderConfirm from "Components/Popup/Confirm/Container/RenderConfirm";
import blank from "Media/blank-profile-picture.png";
import excelDocument from "Media/icons/excelDocument.png";
import firebase from "firebase/compat/app";
import moment from "moment-timezone";
import options from "Media/icons/feather-more-vertical.svg";
import pdfDocument from "Media/icons/pdfDocument.png";
import playbutton from "Media/play-button.webp";
import pptDocument from "Media/icons/pptx.png";
import unknownFile from "Media/icons/unknownFile.png";
import { useSelector } from "react-redux";
import wordDocument from "Media/icons/wordDocument.png";

const ChatCard = React.memo(({ data, myId, group_data, fetchChatMessages }) => {
  const pToken = localStorage.getItem("token");
  const { sentBy, sentTo, message, imageData, createdAt, id } = data;
  const my_image = useSelector((state) => state.authenticationReducer?.image);
  const empId = useSelector((state) => state.renderChatProfileReducer);
  const [image, setImage] = useState(
    "https://placehold.co/600x400/?text=Loading...&font=lato"
  );
  const [video, setVideo] = useState("");
  const [showPreviewPopup, setShowPreviewPopup] = useState(false);
  const [type, setType] = useState("");
  const [fileExt, setFileExt] = useState("");
  const [fileName, setFileName] = useState("");
  const [filePath, setFilePath] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [confirmPopup, setConfirmPopup] = useState(false);
  const [fileId, setFileId] = useState("");
  const [fileDelete, setFileDelete] = useState("");
  const screenId = useMemo(() => empId.toString(), [empId]);
  const open = Boolean(anchorEl);

  const other_image = useMemo(() => {
    if (Array.isArray(group_data) && group_data[0]?.data) {
      const matchingUser = group_data[0].data.find(
        (item) => item?.id?.toString() === sentBy?.toString()
      );
      return matchingUser?.image || "";
    }

    return group_data?.[0]?.image || "";
  }, [group_data, sentBy]);

  const timeConverted = useMemo(() => {
    const utcDate = moment(createdAt).tz("America/Chicago").format();
    return moment(utcDate).format("hh:mm A");
  }, [createdAt]);

  const setMediaMetadata = useCallback((response) => {
    const fileType = response?.data?.type;
    const fileData = response?.data?.data;
    const fileNameValue = response?.data?.name;

    setFileExt(fileType);
    setFileName(fileNameValue);
    setFileId(id);

    if (fileType === ".mp4") {
      setVideo(fileData);
      setType("video");
      setFilePath("");
      return;
    }

    if (fileType === ".png" || fileType === ".jpg" || fileType === ".jpeg") {
      setImage(fileData);
      setType("image");
      setFilePath("");
      return;
    }

    setFilePath(fileData);
    setType("");
    setVideo("");
  }, [id]);

  const getchatImages = useCallback(
    async (path) => {
      const response = await getChatImagesByPath(pToken, path);
      setMediaMetadata(response);
    },
    [pToken, setMediaMetadata]
  );

  useEffect(() => {
    if (imageData) {
      getchatImages(imageData);
    }
  }, [getchatImages, imageData]);

  const chatFilePreview = useCallback(() => {
    setShowPreviewPopup(true);
  }, []);

  const handleMenuOpen = useCallback((event) => {
    setAnchorEl(event?.currentTarget ?? null);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const renderFileImage = useCallback(() => {
    switch (fileExt?.split(".").at(1)) {
      case "doc":
      case "docx":
        return wordDocument;
      case "xls":
      case "xlsx":
      case "csv":
        return excelDocument;
      case "pdf":
        return pdfDocument;
      case "ppt":
      case "pptx":
        return pptDocument;
      default:
        return unknownFile;
    }
  }, [fileExt]);

  const deleteChatFile = useCallback((itemId) => {
    setFileDelete(itemId);
    setConfirmPopup(true);
  }, []);

  const deletechatFilesServer = useCallback(() => {
    const chatFiles = deleteFilesServer(
      pToken,
      type === "image" ? image : type === "video" ? video : filePath
    );
    chatFiles.then((response) => response);
  }, [filePath, image, pToken, type, video]);

  const deleteChatFileData = useCallback(
    (option) => {
      if (option?.toString() === "Yes") {
        let docid = "";
        if (screenId.includes("-")) {
          docid = empId;
        } else {
          docid = sentTo > sentBy ? sentBy + "-" + sentTo : sentTo + "-" + sentBy;
        }

        const chatMessage = firebase.firestore();
        chatMessage
          .collection("chatroom")
          .doc(docid)
          .collection("messages")
          .doc(fileDelete)
          .delete()
          .then(() => {
            setConfirmPopup(false);
            deletechatFilesServer();
            fetchChatMessages();
          });
      }
    },
    [deletechatFilesServer, empId, fetchChatMessages, fileDelete, screenId, sentBy, sentTo]
  );

  const fileDownloadUrl = type === "image" ? image : type === "video" ? video : filePath;
  const fileDisplayName = fileName?.split("-").at(-1);

  const DownloadChatFiles = useCallback(
    (event) => {
      event?.preventDefault?.();
      if (!fileDownloadUrl) return;

      fetch(fileDownloadUrl)
        .then((resp) => resp.blob())
        .then((blob) => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.style.display = "none";
          a.href = url;
          a.download = fileDisplayName;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
        })
        .catch(() => {});
    },
    [fileDisplayName, fileDownloadUrl]
  );

  const isSentByMe = sentBy?.toString() === myId?.toString();
  const isReceivedByMe = sentTo?.toString() === myId?.toString() || sentTo?.toString() === "all";
  const hasDeletePermission = sentBy?.toString() === myId?.toString();
  const isImageType = type === "image";
  const isVideoType = type === "video";
  const fileTypeLabel = isImageType ? "image" : isVideoType ? "video" : "";
  const filePreviewSource = useMemo(
    () => (isImageType ? image : video),
    [image, isImageType, video]
  );
  const menuPaperSx = useMemo(
    () => ({
      overflow: "visible",
      filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
      mt: 1.5,
      "& .MuiAvatar-root": {
        width: 32,
        height: 32,
        ml: -0.5,
        mr: 1,
      },
      "&:before": {
        content: '""',
        display: "block",
        position: "absolute",
        top: 0,
        right: 14,
        width: 10,
        height: 10,
        bgcolor: "background.paper",
        transform: "translateY(-50%) rotate(45deg)",
        zIndex: 0,
      },
    }),
    []
  );
  const renderMessageContent = useMemo(
    () =>
      message?.includes("&*&")
        ? message?.split("&*&").map((curr, index) => <div key={index}>{curr}</div>)
        : message,
    [message]
  );
  const renderMediaContent = useCallback(
    (allowDelete = false) => (
      <>
        {isImageType ? (
          <img
            src={image}
            alt="Media"
            onClick={chatFilePreview}
            style={{ cursor: "pointer" }}
          />
        ) : isVideoType ? (
          <img
            src={playbutton}
            alt="Media"
            onClick={chatFilePreview}
            style={{ cursor: "pointer" }}
          />
        ) : (
          <Link
            className="chat-file"
            to="file-viewer"
            target="_blank"
            onClick={() => {
              localStorage.setItem("filePath", filePath);
              localStorage.setItem("fileType", fileExt?.split(".").at(1));
            }}
          >
            <img src={renderFileImage()} alt="fileImage" className="chat-file-image" />
            <p className="chat-file-text">{fileDisplayName}</p>
          </Link>
        )}
        <IconButton className="chat-file-option">
          <img
            src={options}
            alt="Menu options"
            onClick={handleMenuOpen}
          />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          id="account-menu"
          open={open}
          onClose={handleMenuClose}
          onClick={handleMenuClose}
          className="hello"
          slotProps={{
            paper: {
              elevation: 0,
              sx: menuPaperSx,
            },
          }}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          <MenuItem className="chat-screen-menuItem">
            <a
              className="file-download-text"
              onClick={DownloadChatFiles}
              href={fileDownloadUrl}
            >
              <DownloadIcon />
              Download
            </a>
          </MenuItem>
          {allowDelete && (
            <>
              <Divider />
              {hasDeletePermission && (
                <MenuItem
                  className="chat-screen-menuItem"
                  onClick={() => deleteChatFile(fileId)}
                >
                  <DeleteIcon />
                  Delete
                </MenuItem>
              )}
            </>
          )}
        </Menu>
      </>
    ),
    [
      DownloadChatFiles,
      anchorEl,
      chatFilePreview,
      deleteChatFile,
      fileDownloadUrl,
      fileExt,
      fileId,
      fileDisplayName,
      hasDeletePermission,
      image,
      isImageType,
      isVideoType,
      menuPaperSx,
      open,
      renderFileImage,
      filePath,
      video,
    ]
  );

  const renderPreviewPopup = useCallback(
    () =>
      showPreviewPopup ? (
        <FilePreviewPopup
          path={filePreviewSource}
          hide={setShowPreviewPopup}
          fileType={fileTypeLabel}
        />
      ) : null,
    [filePreviewSource, fileTypeLabel, showPreviewPopup]
  );

  if (isSentByMe) {
    return (
      <div className="right-side-chat">
        <div className="chat-avatar">
          <img
            src={my_image || blank}
            alt="Avatar"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = blank;
            }}
          />
        </div>

        {message ? (
          <li>
            {renderMessageContent}
            <span className="timeconverted">{timeConverted || null}</span>
          </li>
        ) : imageData ? (
          <li className="chat-media">
            <div className="chat-media-body">{renderMediaContent(true)}</div>
            <span className="timeconverted">{timeConverted || null}</span>
          </li>
        ) : null}

        {renderPreviewPopup()}
        {confirmPopup ? (
          <RenderConfirm
            message="Delete it?"
            data="Yes"
            status={deleteChatFileData}
            hide={setConfirmPopup}
          />
        ) : null}
      </div>
    );
  }

  if (isReceivedByMe) {
    return (
      <div className="left-side-chat">
        <div className="chat-avatar">
          <img
            src={other_image || blank}
            alt="Avatar"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = blank;
            }}
          />
        </div>

        {message ? (
          <li>
            {renderMessageContent}
            <span className="timeconverted">{timeConverted || null}</span>
          </li>
        ) : imageData ? (
          <li className="chat-media">
            <div className="chat-media-body">{renderMediaContent(false)}</div>
            <span className="timeconverted">{timeConverted || null}</span>
          </li>
        ) : null}

        {renderPreviewPopup()}
      </div>
    );
  }

  return null;
});

export default ChatCard;
